import { Box, Input, Grid, Divider, Skeleton, Link as MuiLink } from '@mui/material'
import { capitalize } from 'lodash'
import { useMemo, useEffect } from 'react'
import { NavLink } from 'react-router'
import { Button, Logger, Section, Typography } from '@wolfdanepug/common-react'
import { useQuery } from '../lib/useQuery'
import { GetUnstableData } from '../graphql/docs'
import ActionToggle from '../components/ActionToggle'
import DiscountContext from '../components/PlanRecommendation/DiscountContext'
import DiscountPrice from '../components/PlanRecommendation/DiscountPrice'
import ActiveSegment from '../components/ActiveSegment'
import PlanReview from '../components/PlanReview'

export default function Plan() {
	const { data: onboardingData, loading } = useQuery(GetUnstableData, { context: 'onboarding' })

	const data = useMemo(() => {
		return onboardingData?.data
	}, [onboardingData])

	useEffect(() => {
		Logger.log('[Page] Plan', data)
	}, [data])

	const { profile, key }: any = useMemo(() => {
		const dataKey = data?.dogs?.[data.activeDogContext]
		Logger.log('dataKey', dataKey)
		if (!dataKey) return { profile: undefined, key: undefined }
		const dogData = data?.dog[dataKey]
		return { profile: dogData, key: dataKey }
	}, [data])

	const { product, discount, dailyPrice, priceStyles, petName } = useMemo(() => {
		const discountConfiguration = data?.discountConfiguration?.find(
			(d: any) => d.applyTo === 'subtotal'
		)
		const recommendedProduct = profile?.recommendedProduct
		return {
			product: recommendedProduct,
			petName: capitalize(profile?.name) ?? '',
			discount: discountConfiguration ?? null,
			dailyPrice: recommendedProduct?.price ? recommendedProduct.price / 7 / 100 : 0,
			priceStyles: discountConfiguration ? styles.discounted : {}
		}
	}, [profile])

	if (loading) return <Skeleton variant="rectangular" height={400} />

	return (
		<>
			{product?.id && <Input type="hidden" name="products[0]" value={product?.id} />}
			<ActionToggle />
			<Section sx={styles.hero}>
				<Box sx={{ position: 'relative', zIndex: 3 }}>
					<Grid container spacing={3}>
						<Grid item md={5}>
							<Typography variant="h1" sx={{ fontSize: '2.6rem' }} loading={!petName}>
								{petName}'s
								<br /> Recommended Plan
							</Typography>
							<Typography variant="caption" loading={!product?.name}>
								{product?.name}{' '}
								{/* <MuiLink component={Link} href={`/recipes/${product?.sku}`}>
							Nutritional Facts
						</MuiLink> */}
							</Typography>

							<Typography variant="body1" loading={isNaN(dailyPrice)}>
								{discount ? <DiscountContext context={'days'} duration={30} /> : 'Only'}
								<Box component="span" sx={priceStyles}>
									$ {dailyPrice.toFixed(2)}/day
								</Box>{' '}
								{discount && (
									<>
										{/* <Box
											component="br"
											sx={{
												display: {
													xs: 'block',
													md: 'none'
												}
											}}
										/> */}
										<DiscountPrice
											price={dailyPrice}
											discount={discount?.percent}
											context={'percent'}
										/>
									</>
								)}
							</Typography>
							<Divider sx={styles.divider} color="#F88D58" />
							<Typography variant="body2" loading={!product?.name || !petName}>
								Based on your responses, we recommend our {product?.name} recipe. Made with highly
								digestible human grade meat, grains and fresh vegetables, this recipe is perfect for
								dogs like {petName}.
							</Typography>
							<Button
								variant="contained"
								color="primary"
								size="large"
								sx={styles.continue}
								type="submit"
							>
								Continue with Recommendation
							</Button>

							<Typography>
								<MuiLink component={NavLink} to={`/dogs/${key}/plan/build`}>
									Or Select Your Own Plan
								</MuiLink>
							</Typography>
						</Grid>
						<Grid item md={1} />
						{product?.image ? (
							<Grid item md={6} textAlign={'right'}>
								<Box
									component={'img'}
									src={product.image}
									alt="Plan Recommendation"
									sx={{ width: '100%' }}
								/>
							</Grid>
						) : (
							<Skeleton variant="rectangular" width={400} height={400} />
						)}
					</Grid>
				</Box>
			</Section>

			<ActiveSegment segment="plan-details" />
			<PlanReview />
		</>
	)
}

const styles = {
	hero: {
		root: {
			width: '100%',
			backgroundColor: '#fff',
			pt: 12,
			pb: '200px'
		}
	},
	continue: {
		mt: 2,
		mb: 2
	},
	divider: {
		width: 100,
		mt: 2,
		mb: 2
	},
	imageContainer: {
		textAlign: 'right',
		'& img': {
			float: 'none'
		}
	},
	discounted: {
		textDecoration: 'line-through'
	},
	green: {
		color: 'green'
	}
}
